'use client';

import { useRef, useState } from 'react';

import { useTranslations } from 'next-intl';

import { PinPopoverCommentBody } from './PinPopoverCommentBody';
import { PinPopoverHeader } from './PinPopoverHeader';
import { PinPopoverRepliesSection } from './PinPopoverRepliesSection';
import { PinPopoverReplyInput } from './PinPopoverReplyInput';
import { usePinPopoverInteractions } from './usePinPopoverInteractions';
import { usePinPopoverPosition } from './usePinPopoverPosition';

import type { CommentUserInfo, Pin } from '@/types/feedback';

interface PinPopoverProps {
  pin: Pin;
  pinIndex: number;
  commenterInfoMap?: Record<string, CommentUserInfo>;
  /** Display name for replies by a Wix user (the agency or a collaborator). */
  agencyName?: string;
  /** Viewport-relative position of the pin badge center */
  anchorX: number;
  anchorY: number;
  /** Width taken up on the right, e.g. the open comments panel. */
  rightInset?: number;
  onClose: () => void;
  onSave: () => void;
  onUpdateLabel: (label: string) => void;
  onDelete: () => void;
  onAddReply?: (message: string) => void;
  onDeleteReply?: (replyIndex: number) => void;
  onResolve?: () => void;
  onReopen?: () => void;
  canDelete?: boolean;
  canEdit?: boolean;
  currentUserId?: string | null;
}

export function PinPopover({
  pin,
  pinIndex,
  commenterInfoMap,
  agencyName,
  anchorX,
  anchorY,
  rightInset,
  onClose,
  onSave,
  onUpdateLabel,
  onDelete,
  onAddReply,
  onDeleteReply,
  onResolve,
  onReopen,
  canDelete = true,
  canEdit = true,
  currentUserId,
}: PinPopoverProps) {
  const t = useTranslations();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [isEditing, setIsEditing] = useState(!pin.label?.trim());
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const { left, verticalStyle, width, maxHeight } = usePinPopoverPosition(
    anchorX,
    anchorY,
    rightInset,
  );

  usePinPopoverInteractions({
    isEditing,
    hasExistingLabel: !!pin.label?.trim(),
    onClose,
    popoverRef,
    menuRef,
    textareaRef,
    setIsMenuOpen,
    setIsEditing,
  });

  function handleSave() {
    onSave();
    setIsEditing(false);
  }

  function handleEditFromMenu() {
    setIsEditing(true);
    setIsMenuOpen(false);
  }

  function handleDeleteFromMenu() {
    setIsMenuOpen(false);
    onDelete();
  }

  function handleResolveFromMenu() {
    setIsMenuOpen(false);
    onResolve?.();
  }

  function handleReopenFromMenu() {
    setIsMenuOpen(false);
    onReopen?.();
  }

  return (
    <div
      ref={popoverRef}
      role="dialog"
      data-hook="pin-popover"
      aria-label={t('get-feedback-pinPopover-ariaLabel', { pinIndex })}
      style={{
        position: 'fixed',
        left,
        ...verticalStyle,
        width,
        maxHeight,
        zIndex: 9999,
      }}
      className="flex flex-col rounded-xl border border-border bg-background shadow-2xl"
    >
      <PinPopoverHeader
        commenterInfo={
          pin.commenterId ? commenterInfoMap?.[pin.commenterId] : undefined
        }
        createdAt={pin.createdAt}
        commentId={pin.commentId}
        isResolved={pin.status === 'RESOLVED'}
        isMenuOpen={isMenuOpen}
        menuRef={menuRef}
        canDelete={canDelete}
        canEdit={canEdit}
        canResolve={!!onResolve}
        canReopen={!!onReopen}
        onToggleMenu={() => setIsMenuOpen((open) => !open)}
        onEdit={handleEditFromMenu}
        onDelete={handleDeleteFromMenu}
        onResolve={handleResolveFromMenu}
        onReopen={handleReopenFromMenu}
        onClose={onClose}
      />

      <div className="min-h-0 flex-1 overflow-y-auto">
        <PinPopoverCommentBody
          canEdit={canEdit}
          isEditing={isEditing}
          isResolved={pin.status === 'RESOLVED'}
          label={pin.label}
          textareaRef={textareaRef}
          onUpdateLabel={onUpdateLabel}
          onSave={handleSave}
          onStartEditing={() => setIsEditing(true)}
        />

        <PinPopoverRepliesSection
          replies={pin.replies ?? []}
          commenterId={pin.commenterId}
          commenterInfoMap={commenterInfoMap}
          agencyName={agencyName}
          currentUserId={currentUserId}
          isResolved={pin.status === 'RESOLVED'}
          onDeleteReply={onDeleteReply}
        />
      </div>

      {onAddReply && pin.commentId && !isEditing && (
        <PinPopoverReplyInput onAddReply={onAddReply} />
      )}
    </div>
  );
}
